import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';
import diff from 'object-diff';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { setColumnWidth } from 'services/columnWidths/actions';
import {
    requestedCreateEquipment,
    requestedUpdateEquipment,
    requestedDeleteEquipment
} from 'services/equipment/actions';
import { PostEquipmentPayload } from 'services/equipment/types';

import VirtualizedTable from 'components/VirtualizedTable';

import {
    selectEquipmentTableColumns,
    selectIsWritable,
    makeSelectEquipmentItems
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    isWritable?: ReturnType<typeof selectIsWritable>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    requestedCreateEquipment: typeof requestedCreateEquipment;
    requestedUpdateEquipment: typeof requestedUpdateEquipment;
    requestedDeleteEquipment: typeof requestedDeleteEquipment;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class EquipmentVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleCreate = (item: VirtualizedTableItem): void => {
        const { requestedCreateEquipment: createEquipment } = this.props;

        createEquipment({
            ...item,
            internalId: item.internalId ?? undefined
        } as PostEquipmentPayload);
    };

    handleItemUpdate = (item: VirtualizedTableItem): void => {
        const { requestedUpdateEquipment: updateEquipment, items } = this.props;
        const { key, id, equipmentType, nozzleType, ...values } = item;
        const equipmentSet = items.find((eqSet) => eqSet.id === id);

        if (equipmentSet) {
            const payload = diff(equipmentSet, values);

            if (payload && Object.keys(payload).length) {
                updateEquipment(id, payload);
            }
        }
    };

    handleDelete = (items: VirtualizedTableItem[]): void => {
        const { requestedDeleteEquipment: deleteEquipment } = this.props;

        if (items[0] && items[0].id) {
            deleteEquipment(items[0].id);
        }
    };

    handleHiddenFormFields = (item: VirtualizedTableItem): string[] => {
        const hiddenFormFields = [];

        if (item.equipmentType !== 'SPRAYER') {
            hiddenFormFields.push('tankVolume');
        }

        if (item.equipmentType !== 'NOZZLESET') {
            hiddenFormFields.push('nozzleType');
        }

        return hiddenFormFields;
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, isWritable } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                label="Equipment"
                labelPlural="Equipment"
                editable={isWritable}
                enableCreating={isWritable}
                enableItemEditing={isWritable}
                enableDeleting={isWritable}
                selectable={isWritable}
                onCreate={this.handleCreate}
                onItemUpdate={this.handleItemUpdate}
                onDelete={this.handleDelete}
                hiddenFormFields={this.handleHiddenFormFields}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectEquipmentItems(),
    columns: selectEquipmentTableColumns,
    isWritable: selectIsWritable
});

export default connect(mapStateToProps, {
    setColumnWidth,
    requestedCreateEquipment,
    requestedUpdateEquipment,
    requestedDeleteEquipment
})(EquipmentVirtualizedTable);
